// Content moderation utility for property listings

// Banned words: sexual, political, hate speech (Arabic + English)
const BANNED_WORDS: string[] = [
  // Sexual (Arabic)
  'جنس', 'سكس', 'إباحي', 'عاهرة', 'بغاء', 'فاحشة', 'عري', 'خليع', 'فاجر', 'داعر',
  'شهواني', 'مثير', 'إغراء', 'تحرش', 'اغتصاب', 'زنا',
  // Sexual (English)
  'sex', 'porn', 'nude', 'naked', 'erotic', 'xxx', 'adult', 'escort', 'prostitut',
  'sexual', 'horny', 'fuck', 'dick', 'pussy', 'ass', 'boob', 'breast',
  // Political (Arabic)
  'انقلاب', 'ثورة', 'مظاهرة', 'احتجاج', 'معارضة', 'إرهاب', 'داعش', 'القاعدة',
  'حزب', 'سياسي', 'انتخاب', 'رئيس', 'حكومة', 'دولة إسلامية', 'جهاد',
  // Political (English)
  'terrorist', 'terrorism', 'isis', 'jihad', 'coup', 'revolution', 'protest',
  'political', 'election', 'government overthrow',
  // Hate speech (Arabic)
  'كافر', 'مشرك', 'عنصري', 'تمييز', 'كراهية', 'ازدراء', 'احتقار', 'نازي',
  'يهودي خنزير', 'عربي كلب', 'أسود قرد',
  // Hate speech (English)
  'hate', 'racist', 'racism', 'nazi', 'nigger', 'faggot', 'slur', 'bigot',
  'white supremac', 'ethnic cleansing', 'genocide',
];

/**
 * Check if text contains banned words.
 * Returns the first found banned word or null if clean.
 */
export function checkBannedWords(text: string): string | null {
  if (!text) return null;
  const lower = text.toLowerCase();
  for (const word of BANNED_WORDS) {
    if (lower.includes(word.toLowerCase())) {
      return word;
    }
  }
  return null;
}

/**
 * Validate image file for real estate listing:
 * - Must be an image
 * - Max 1MB
 * Returns an error message string or null if valid.
 */
export function validateListingImage(file: File): string | null {
  if (!file.type.startsWith('image/')) {
    return 'يُسمح فقط بملفات الصور (PNG, JPG, WEBP)';
  }
  const MAX_SIZE = 1 * 1024 * 1024; // 1MB
  if (file.size > MAX_SIZE) {
    return 'حجم الصورة يجب أن لا يتجاوز 1 ميجابايت';
  }
  return null;
}

/**
 * Real estate related keywords to validate image name/context.
 * We do a basic filename heuristic — actual AI-based image content check
 * would require a backend vision API call.
 */
const NON_REALESTATE_PATTERNS = [
  /selfie/i, /portrait/i, /person/i, /people/i, /face/i,
  /food/i, /meal/i, /restaurant/i, /car/i, /vehicle/i,
  /animal/i, /dog/i, /cat/i, /pet/i, /nature/i, /beach/i,
  /screenshot/i, /meme/i, /logo/i, /flag/i, /map/i,
];

/**
 * Basic filename-based heuristic to flag obviously non-real-estate images.
 * Returns an error message or null if acceptable.
 */
export function checkImageRelevance(file: File): string | null {
  const name = file.name.toLowerCase();
  for (const pattern of NON_REALESTATE_PATTERNS) {
    if (pattern.test(name)) {
      return 'يرجى رفع صورة تخص العقار فقط (مبنى، شقة، غرفة، مدخل، إلخ)';
    }
  }
  return null;
}
